import { useEffect, useState } from "react";
import { MapPin, ChevronLeft, FileText, ExternalLink } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { http } from "@/lib/api";

const RULES = [
  "Regional centre 600 km from the capital; 40-bed hospital with 4 Special Care Unit beds — no HDU, ICU or NICU.",
  "5 RG anaesthetists (you are one), 4 GP obstetricians, 2 surgeons + 1 surgical registrar.",
  "Red cells, FFP and cryo available; NO platelets on site and NO ROTEM.",
  "Retrieval is aeromedical via a central service — about 2 hours from dispatch.",
  "Always state: can this be safely managed here, or does it need transfer?",
];

const Bullet = ({ children }) => (
  <li className="text-sm flex gap-2">
    <span className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: "var(--accent)" }} />
    <span>{children}</span>
  </li>
);

const Detail = ({ d, onBack }) => (
  <div data-testid="scenarioville-detail" className="fade-up">
    <button data-testid="scenarioville-detail-back" onClick={onBack} className="mt-2 text-sm flex items-center gap-1" style={{ color: "var(--accent)" }}>
      <ChevronLeft size={14} /> Back to summary
    </button>
    <h3 className="mt-4 font-display text-lg font-bold">{d.title}</h3>
    <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--ink-2)" }}>{d.intro}</p>
    {d.sections.map((s) => (
      <section key={s.heading} className="mt-6" data-testid={`scenarioville-section-${s.heading.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
        <h4 className="font-display font-semibold text-sm uppercase tracking-wider" style={{ color: "var(--accent)" }}>{s.heading}</h4>
        <ul className="mt-2 space-y-2">{s.items.map((it) => <Bullet key={it}>{it}</Bullet>)}</ul>
      </section>
    ))}
    {d.images?.length > 0 && (
      <section className="mt-6" data-testid="scenarioville-images">
        <h4 className="font-display font-semibold text-sm uppercase tracking-wider" style={{ color: "var(--accent)" }}>Cognitive Aids</h4>
        <p data-testid="scenarioville-image-credit" className="mt-1 text-xs" style={{ color: "var(--ink-3)" }}>Images by {d.image_credit}. Tap an image to open it full size.</p>
        <div className="mt-3 space-y-4">
          {d.images.map((img, k) => (
            <figure key={img.src}>
              <a href={img.src} target="_blank" rel="noreferrer" data-testid={`scenarioville-image-${k}`}>
                <img src={img.src} alt={img.alt} loading="lazy" className="w-full rounded-lg border" style={{ borderColor: "var(--line)" }} />
              </a>
              <figcaption className="mt-1 text-xs" style={{ color: "var(--ink-3)" }}>{img.alt}. Credit: {d.image_credit}</figcaption>
            </figure>
          ))}
        </div>
      </section>
    )}
    <p className="mt-8 text-xs" style={{ color: "var(--ink-3)" }}>
      Source: {d.source}.{" "}
      <a data-testid="scenarioville-source-link" href={d.url} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1">
        Open ANZCA PDF <ExternalLink size={11} />
      </a>
    </p>
  </div>
);

export const ScenariovilleSheet = () => {
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(false);
  useEffect(() => {
    http.get("/scenarioville").then((r) => setData(r.data)).catch(() => {});
  }, []);
  return (
    <Sheet onOpenChange={(o) => !o && setDetail(false)}>
      <SheetTrigger asChild>
        <button data-testid="scenarioville-info-trigger" className="btn-ghost h-9 px-3 rounded-full text-sm flex items-center gap-1.5">
          <MapPin size={14} /> <span className="hidden sm:inline">Scenarioville</span>
        </button>
      </SheetTrigger>
      <SheetContent data-testid="scenarioville-sheet" className="overflow-y-auto sm:max-w-md" style={{ background: "var(--surface)" }}>
        <SheetHeader>
          <SheetTitle className="font-display">Scenarioville</SheetTitle>
        </SheetHeader>
        {detail && data ? <Detail d={data.detail} onBack={() => setDetail(false)} /> : (
          <>
            <p className="mt-4 text-sm leading-relaxed" style={{ color: "var(--ink-2)" }}>{data?.text}</p>
            <ul className="mt-6 space-y-3">{RULES.map((r) => <Bullet key={r}>{r}</Bullet>)}</ul>
            <button data-testid="scenarioville-detailed-info-link" onClick={() => setDetail(true)} disabled={!data}
              className="mt-8 btn-primary h-10 px-4 rounded-full text-sm font-semibold flex items-center gap-2">
              <FileText size={15} /> Detailed Information
            </button>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
